import { useEffect, useState } from "react";
import { SettingsNavigationProp, routeSettings, routeHome, routeDataManagementSettingsDetails, routeDataManagementSettings } from "../../Routes";
import { useNavigation } from "@react-navigation/native";
import OtherDataComponent from "../../components/OtherDataComponent";
import LegalTermsConditions from "../../components/LegalTermsConditions";
import PrivacyPolicy from "../../components/PrivacyPolicy";
import TopAppbar from "../../components/TopAppBar";
import backIcon from "../../assets/TopNavBarIcon/icon_nav_back.svg";
import closeIcon from "../../assets/TopNavBarIcon/icon_nav_close.svg";
import AcceptDataComponent from "../../components/AcceptDataComponent";
import { Legals } from "../../models/Legals";
import { AsyncStorageKeys, Pages, SubPages, Type } from "../../Types";
import useGenericContentStore from "../../store/genericContent.store";
import { AcceptOtherData } from "../../models/AcceptOtherData";
import { getDataMngButtonTitle, globalAny } from "../../utils/Utils";
import usePaymentTransactionStore from "../../store/usePaymentTransaction.store";
import usePrivacyPolicy from "../../hooks/usePrivacyPolicy";
import { StyledContainer } from "./styled";
import useToggleStore from "../../store/useToggle.store";
import { AppLogger } from "../../utils/AppLogger";

const SettingsDataManagement = (props: any) => {
	const { isNarrow } = useGenericContentStore((state) => ({ isNarrow: state.isNarrow }));

	// Props
	const { location, subLocation } = props.route.params;

	// Navigation
	const navigationSettingsScreen = useNavigation<SettingsNavigationProp>();
	const navigation = useNavigation();

	// Zustand store values
	const { transactionHistory } = usePaymentTransactionStore((state: any) => ({
		transactionHistory: state.transactionHistory,
	}));

	const { setSelectedTabs } = useGenericContentStore((state: any) => ({
		setSelectedTabs: state.setSelectedTabs,
	}));

	const { isToggleHideDataMgmt } = useToggleStore((state: any) => ({
		isToggleHideDataMgmt: state.isToggleHideDataMgmt,
	}));

	// State: strings
	const [state, setState] = useState("");
	const [googleAnalytics, setGoogleAnalytics] = useState("");
	const [videoAnalytics, setVideoAnalytics] = useState("");
	const [sonyPictures, setSonyPictures] = useState("");
	const [sonyCompany, setSonyCompany] = useState("");
	const [acceptDataTitle, setAcceptDataTitle] = useState("");
	const [acceptDataDescription, setAcceptDataDescription] = useState("");
	const [otherDataTitle, setOtherDataTitle] = useState("");
	const [otherDataDescription, setOtherDataDescription] = useState("");

	// State: booleans
	const [legal, setLegal] = useState(false);
	const [googleScreen, setGoogleScreen] = useState(false);
	const [videoAnalyticsScreen, setVideoAnalyticsScreen] = useState(false);
	const [otherData, setOtherData] = useState(false);
	const [googleData, setGoogleAnalyticsData] = useState(false);
	const [videoAnalyticsData, setVideoAnalyticsData] = useState(false);
	const [transactionsValue, setTransactionsValue] = useState(false);
	const [enabledSonyPicture, setEnabledSonyPicture] = useState(false);
	const [enabledSonyGroup, setEnabledSonyGroup] = useState(false);
	const [enabledGoogleAnalytics, setEnabledGoogleAnalytics] = useState(false);
	const [enabledVideoAnalytics, setEnabledVideoAnalytics] = useState(false);

	// State: objects/data
	const [legalText, setLegalText] = useState<Legals>();
	const [legalData, setLegalData] = useState<Legals>();
	const [acceptOtherData, setAcceptOtherData] = useState<AcceptOtherData>();

	// Custom hooks
	const privacyPolicy = usePrivacyPolicy();

	/**
	 * Fetches and sets the accept data title and description for display.
	 *
	 * - Attempts to retrieve `option-title` and `option-description` from the current `acceptOtherData` state.
	 * - If not available, falls back to cached data stored in localStorage under `cacheAcceptOtherDataSettings`.
	 * - Extracts the relevant text values and updates state variables for title and description.
	 */
	const fetchAcceptData = () => {
		let titleData = acceptOtherData?.sections
			? acceptOtherData?.sections[0]?.options[0]?.optionsWordings?.find((val: any) => val?.key === "option-title")
			: null;
		let titleDescription = acceptOtherData?.sections
			? acceptOtherData?.sections[0]?.options[0]?.optionsWordings?.find((val: any) => val?.key === "option-description")
			: null;
		if (!titleData) {
			const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataSettings) ?? "{}");
			titleData = cacheAcceptOtherData?.sections
				? cacheAcceptOtherData?.sections[0]?.options[0]?.optionsWordings?.find((val: any) => val?.key === "option-title")
				: null;
		}
		if (!titleDescription) {
			const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataSettings) ?? "{}");
			titleDescription = cacheAcceptOtherData?.sections
				? cacheAcceptOtherData?.sections[0]?.options[0]?.optionsWordings?.find((val: any) => val?.key === "option-description")
				: null;
		}
		setAcceptDataTitle(titleData?.textValue);
		setAcceptDataDescription(titleDescription?.textValue);
	};

	/**
	 * Fetches and sets the title and description for the "other data" section.
	 *
	 * - Tries to extract `section-title` and `section-description` from the nested
	 *   `sectionWording` inside `acceptOtherData`.
	 * - If the data is not available, falls back to cached data from localStorage.
	 * - Updates the state with the fetched title and description text values.
	 */
	const fetchOtherData = () => {
		let titleData = acceptOtherData?.sections
			? acceptOtherData?.sections[0]?.childSections[0]?.sectionWording?.find((val: any) => val?.key === "section-title")
			: null;
		let titleDescription = acceptOtherData?.sections
			? acceptOtherData?.sections[0]?.childSections[0]?.sectionWording?.find((val: any) => val?.key === "section-description")
			: null;
		if (!titleData) {
			const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataSettings) ?? "{}");
			titleData = cacheAcceptOtherData?.sections
				? cacheAcceptOtherData?.sections[0]?.childSections[0]?.sectionWording?.find((val: any) => val?.key === "section-title")
				: null;
		}
		if (!titleDescription) {
			const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataSettings) ?? "{}");
			titleDescription = cacheAcceptOtherData?.sections
				? cacheAcceptOtherData?.sections[0]?.childSections[0]?.sectionWording?.find((val: any) => val?.key === "section-description")
				: null;
		}

		setOtherDataTitle(titleData?.textValue);
		setOtherDataDescription(titleDescription?.textValue);
	};

	/**
	 * Navigates to the main settings screen by pushing a new route.
	 */
	const onSubmit = () => {
		navigationSettingsScreen.push(routeSettings, {});
	};

	/**
	 * Handles the back navigation logic based on current location and sub-location.
	 * - If on privacy, or on legal/otherData without a subLocation, navigates directly to settings screen.
	 * - Otherwise, if navigation stack allows, goes back and resets tab if in transactions.
	 * - If cannot go back, navigates to settings screen and resets tab if in transactions.
	 */
	const onBackPress = () => {
		if (location === Pages.privacy || (location === Pages.legal && !subLocation) || (location === Pages.otherData && !subLocation)) {
			navigationSettingsScreen.navigate(routeSettings, {});
			return;
		}

		if (navigation.canGoBack()) {
			location === Pages.transactions && setSelectedTabs(0);
			navigation.goBack();
			return;
		}

		location === Pages.transactions && setSelectedTabs(0);
		navigationSettingsScreen.navigate(routeSettings, {});
	};

	/**
	 * Handles back press action for "Other Data" screens by going back if possible.
	 */
	const onBackPressOtherData = () => {
		if (navigation.canGoBack()) {
			navigation.goBack();
		}
	};

	/**
	 * Loads and displays legal content based on the selected key and type.
	 * Falls back to cached legal data if live data is unavailable.
	 * Sets the current state and legal text content based on the selected legal item.
	 *
	 * @param {string} keyValue - The legal item key (e.g. promotional terms, terms of use).
	 * @param {number} type - The type identifier used to filter legal contents.
	 */
	const onLegal = async (keyValue: string, type: number) => {
		let data = legalData?.legalContents?.filter((val: any) => val?.type === type);
		if (!data) {
			const cacheLegalData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheLegalDataCreate) ?? "{}");
			data = cacheLegalData?.legalContents?.filter((val: any) => val?.type === type);
		}
		if (data) {
			switch (keyValue) {
				case globalAny.language.promotional_terms:
					setState(globalAny.language.promotional_terms);
					setLegalText(data[0]?.htmlContent);
					setLegal(true);
					break;
				case globalAny.language.terms_of_use:
					setState(globalAny.language.terms_of_use);
					setLegalText(data[0]?.htmlContent);
					setLegal(true);
					break;
				case globalAny.language.terms_of_service:
					setState(globalAny.language.terms_of_service);
					setLegalText(data[0]?.htmlContent);
					setLegal(true);
					break;
			}
		}
	};

	/**
	 * Navigates to the "Other Data" page within data management settings,
	 * and hides Google and Video Analytics screens.
	 */
	const onNext = () => {
		setGoogleScreen(false);
		setVideoAnalyticsScreen(false);
		//@ts-ignore
		navigation.push(routeDataManagementSettings, { location: Pages.otherData });
	};

	/**
	 * Initializes and returns the "Sony Picture" text from acceptOtherData or cached data.
	 * Logs an error if data retrieval fails.
	 *
	 * @returns {string | undefined} - The text value for "section-read-more-1".
	 */
	const initSonyPicture = () => {
		try {
			let data = acceptOtherData?.sections[0]?.sectionWording?.filter((val: any) => val?.key === "section-read-more-1");
			if (!data) {
				const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataSettings) ?? "{}");
				data = cacheAcceptOtherData?.sections[0]?.sectionWording?.filter((val: any) => val?.key === "section-read-more-1");
			}
			return data[0]?.textValue;
		} catch (e) {
			AppLogger.error("Error Sony Picture Data Management:", e);
		}
	};

	/**
	 * Initializes and returns the "Sony Company" text from acceptOtherData or cached data.
	 * Logs an error if data retrieval fails.
	 *
	 * @returns {string | undefined} - The text value for "section-read-more-2".
	 */
	const initSonyCompany = () => {
		try {
			let data = acceptOtherData?.sections[0]?.sectionWording?.filter((val: any) => val?.key === "section-read-more-2");
			if (!data) {
				const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataSettings) ?? "{}");
				data = cacheAcceptOtherData?.sections[0]?.sectionWording?.filter((val: any) => val?.key === "section-read-more-2");
			}
			return data[0]?.textValue;
		} catch (e) {
			AppLogger.error("Error Sony Company Data Management:", e);
		}
	};

	/**
	 * Handles press action for Sony Pictures.
	 * Sets the Sony Pictures text and updates the UI state accordingly.
	 */
	const onPressSonyPictures = () => {
		setSonyPictures(initSonyPicture());
		setState("Sony Pictures");
	};

	/**
	 * Handles press action for Sony Group Companies.
	 * Sets formatted Sony Company HTML content and updates the UI state accordingly.
	 */
	const onPressSonyCompany = () => {
		setSonyCompany(`
		  <span style="display: flex; align-items: center;">
			<label target="_blank" style="display: flex; align-items: center; margin-right: 500px; margin-top:-100px;">
			  ${initSonyCompany()}
			</label>
		  </span>
		`);
		setState("Sony Group Companies");
	};

	/**
	 * Handles press action for Google Analytics.
	 * Checks if Google Analytics data is initialized and updates the UI state and screen visibility accordingly.
	 */
	const onPressGoogle = () => {
		const isGoogleAnalytics = initialData("Google Analytics", "option-read-more-1");
		!isGoogleAnalytics && setState(getDataMngButtonTitle("Google Analytics", globalAny.language.google_analytics));
		setGoogleScreen(true);
	};

	/**
	 * Handles press action for Video Analytics.
	 * Checks if Video Analytics data is initialized and updates the UI state and screen visibility accordingly.
	 */
	const onPressVideoAnalytics = () => {
		const isVideoAnalytics = initialData("Video Analytics", "option-read-more-1");
		!isVideoAnalytics && setState(getDataMngButtonTitle("Video Analytics", globalAny.language.video_analytics));
		setVideoAnalyticsScreen(true);
	};

	/**
	 * Retrieves and sets initial data text values based on a given name and key.
	 * - Looks up `acceptOtherData` or cached data for matching options.
	 * - Sets Google Analytics or Video Analytics text based on the `name`.
	 * - Updates the current UI state with the option title.
	 *
	 * @param {string} name - The option name to filter by.
	 * @param {string} key - The key to find in the option wordings.
	 * @returns {string | undefined} - The text value found for the key.
	 */
	const initialData = (name: any, key: any) => {
		try {
			let initialData = acceptOtherData?.sections[0]?.childSections[0].options?.filter((val: any) => val?.name === name);
			if (!initialData) {
				const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataSettings) ?? "{}");
				initialData = cacheAcceptOtherData?.sections[0]?.childSections[0].options?.filter((val: any) => val?.name === name);
			}
			const data = initialData[0]?.optionsWordings?.filter((val: any) => val?.key === key);
			const title = initialData[0]?.optionsWordings?.filter((val: any) => val?.key === "option-title");
			name === "Google Analytics" ? setGoogleAnalytics(data[0]?.textValue) : setVideoAnalytics(data[0]?.textValue);
			setState(title[0]?.textValue);
			return data[0]?.textValue;
		} catch (e) {
			AppLogger.error("Error other data:", e);
		}
	};

	/**
	 * Navigates to the Home screen.
	 */
	const onClose = () => {
		//@ts-ignore
		navigation.navigate(routeHome);
	};

	/**
	 * Side effect runs once on mount:
	 * - If location is 'data', sets selected tab to index 2.
	 * - If location is 'acceptData' or 'otherData' and data management toggle is hidden,
	 *   forces tab to 2 and navigates to Settings screen.
	 */
	useEffect(() => {
		if (location === Pages.data) setSelectedTabs(2);

		if ((location === Pages.acceptData || location === Pages.otherData) && isToggleHideDataMgmt) {
			setSelectedTabs(2);
			navigationSettingsScreen.navigate("Settings", {});
		}
	}, []);

	/**
	 * Watches `transactionHistory` changes:
	 * - Updates transactions value state if history exists.
	 * - If transactionHistory is undefined and location is 'transactions', redirects to settings screen.
	 */
	useEffect(() => {
		if (transactionHistory) {
			setTransactionsValue(transactionHistory);
		}

		if (transactionHistory.length === undefined && location === Pages.transactions) {
			navigationSettingsScreen.navigate(routeSettings, {});
		}
	}, [transactionHistory]);

	/**
	 * On component mount, attempts to load cached legal and acceptance data from localStorage
	 * and update corresponding state variables.
	 */
	useEffect(() => {
		(async () => {
			const resultLegal = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheLegalDataCreate) ?? "{}");
			const resultAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataSettings) ?? "{}");
			if (resultLegal && resultAcceptOtherData) {
				setLegalData(resultLegal);
				setAcceptOtherData(resultAcceptOtherData);
			}
		})();
	}, []);

	/**
	 * Watches Google and Video Analytics data state:
	 * - Sets `otherData` flag to false if both are missing.
	 * - Sets `otherData` flag to true if both are present.
	 */
	useEffect(() => {
		if (!googleData && !videoAnalyticsData) {
			setOtherData(false);
		}

		if (googleData && videoAnalyticsData) {
			setOtherData(true);
		}
	}, [googleData, videoAnalyticsData]);

	/**
	 * Watches `otherData` state and updates Google and Video Analytics data accordingly.
	 */
	useEffect(() => {
		setGoogleAnalyticsData(otherData);
		setVideoAnalyticsData(otherData);
	}, [otherData]);

	/**
	 * On component mount, initializes and sets enable flags for:
	 * - Sony Picture and Sony Group Company data.
	 * - Google Analytics and Video Analytics data.
	 * Falls back to false if initialization throws errors.
	 */
	useEffect(() => {
		try {
			setEnabledSonyPicture(initSonyPicture());
			setEnabledSonyGroup(initSonyCompany());
		} catch {
			setEnabledSonyPicture(false);
			setEnabledSonyGroup(false);
		}

		try {
			setEnabledGoogleAnalytics(initialData("Google Analytics", "option-read-more-1"));
			setEnabledVideoAnalytics(initialData("Video Analytics", "option-read-more-1"));
		} catch {
			setEnabledGoogleAnalytics(false);
			setEnabledVideoAnalytics(false);
		}
	}, []);

	/**
	 * Watches `location` and `subLocation` changes:
	 * - Depending on subLocation, loads corresponding legal, Sony, Google or Video Analytics data.
	 * - Depending on location, sets UI state or fetches accept/other data accordingly.
	 */
	useEffect(() => {
		switch (subLocation) {
			case SubPages.termsOfUse:
				onLegal(globalAny.language.terms_of_use, 6);
				return;
			case SubPages.termsOfService:
				onLegal(globalAny.language.terms_of_service, 3);
				return;
			case SubPages.sonyPictures:
				onPressSonyPictures();
				return;
			case SubPages.sonyGroup:
				onPressSonyCompany();
				return;
			case SubPages.googleAnalytics:
				onPressGoogle();
				return;
			case SubPages.videoAnalytics:
				onPressVideoAnalytics();
				return;
		}

		switch (location) {
			case Pages.legal:
				setState(globalAny.language.legal_terms);
				break;
			case Pages.acceptData:
				fetchAcceptData();
				setState("");
				setState(globalAny.language.data_management);
				break;
			case Pages.otherData:
				fetchOtherData();
				setState("");
				setState(globalAny.language.data_management);
				break;
			case Pages.privacy:
				setState(globalAny.language.privacy_policy);
				break;
			case Pages.transactions:
				setState(globalAny.language.transaction_history);
				break;
		}
	}, [location, subLocation]);

	return (
		// @ts-ignores
		<StyledContainer>
			<TopAppbar
				leftIcon={closeIcon}
				leftIconWidth={92}
				leftTitleIcon={backIcon}
				screenTitle={state}
				onPressLeftIcon={onClose}
				onPressLeftTitleIcon={onBackPress}
				isSingleLogo={true}
				isNarrow={isNarrow}
			/>
			{location === Pages.legal && !legal && (
				<LegalTermsConditions
					hideTitle
					bodyText={globalAny.language.i_read_agree}
					onPressTermsOfUse={() =>
						//@ts-ignore
						navigation.push(routeDataManagementSettingsDetails, { location: Pages.legal, subLocation: SubPages.termsOfUse })
					}
					onPressTermsOfService={() =>
						//@ts-ignore
						navigation.push(routeDataManagementSettingsDetails, { location: Pages.legal, subLocation: SubPages.termsOfService })
					}
				/>
			)}

			{location === Pages.legal && legal && <PrivacyPolicy text={legalText}></PrivacyPolicy>}

			{location === Pages.acceptData && !subLocation && (
				<AcceptDataComponent
					onPressPrimaryButton={onNext}
					onPressSecondaryButton={onBackPress}
					onPressSonyCompany={() =>
						//@ts-ignore
						navigation.push(routeDataManagementSettingsDetails, { location: Pages.acceptData, subLocation: SubPages.sonyGroup })
					}
					onPressSonyPictures={() =>
						//@ts-ignore
						navigation.push(routeDataManagementSettingsDetails, { location: Pages.acceptData, subLocation: SubPages.sonyPictures })
					}
					type={Type.Settings}
					title={acceptDataTitle}
					description={acceptDataDescription}
					sonyPictures={enabledSonyPicture}
					sonyCompany={enabledSonyGroup}
					isSettings={true}
				/>
			)}

			{location === Pages.otherData && !subLocation && (
				<OtherDataComponent
					onSubmit={(val) => setOtherData(val)}
					onChangeGoogleData={(val) => setGoogleAnalyticsData(val)}
					onChangeVideoAnalyticsData={(val) => setVideoAnalyticsData(val)}
					onPressPrimaryButton={onSubmit}
					onPressSecondaryButton={() => onBackPressOtherData()}
					onPressGoogle={() =>
						//@ts-ignore
						navigation.push(routeDataManagementSettingsDetails, { location: Pages.otherData, subLocation: SubPages.googleAnalytics })
					}
					onPressVideoAnalytics={() =>
						//@ts-ignore
						navigation.push(routeDataManagementSettingsDetails, { location: Pages.otherData, subLocation: SubPages.videoAnalytics })
					}
					isSettings={true}
					title={otherDataTitle}
					description={otherDataDescription}
					googleAnalytics={enabledGoogleAnalytics}
					videoAnalytics={enabledVideoAnalytics}
				/>
			)}

			{location === Pages.otherData && googleScreen && <PrivacyPolicy text={googleAnalytics}></PrivacyPolicy>}

			{location === Pages.otherData && videoAnalyticsScreen && <PrivacyPolicy text={videoAnalytics}></PrivacyPolicy>}

			{location === Pages.acceptData && subLocation === SubPages.sonyPictures && <PrivacyPolicy text={sonyPictures}></PrivacyPolicy>}

			{location === Pages.acceptData && subLocation === SubPages.sonyGroup && <PrivacyPolicy text={sonyCompany}></PrivacyPolicy>}

			{location === Pages.privacy && <PrivacyPolicy text={privacyPolicy}></PrivacyPolicy>}

			{location === Pages.transactions && <PrivacyPolicy transactionHistory={transactionsValue}></PrivacyPolicy>}
		</StyledContainer>
	);
};

export default SettingsDataManagement;
